import type { OwnerId } from '../ids';

/**
 * Combat rules (Phase 6, DECISIONS D62–D66). Pure functions and constants:
 * no engine, no state. Every damage number in the game goes through
 * `computeDamage`, so the rules are testable in isolation.
 */

/** How an attack hurts. Bullets are poor against armour and structures; melee is not. */
export type DamageType = 'bullet' | 'melee';

/** What is being hit. Buildings are always `structure`. */
export type ArmorClass = 'light' | 'armored' | 'structure';

export const DAMAGE_TYPES: readonly DamageType[] = ['bullet', 'melee'];
export const ARMOR_CLASSES: readonly ArmorClass[] = ['light', 'armored', 'structure'];

/** Multiplier applied to base damage before flat armour is subtracted. Placeholder balance (D63). */
export const DAMAGE_MULTIPLIER: Readonly<Record<DamageType, Readonly<Record<ArmorClass, number>>>> = {
  bullet: { light: 1, armored: 0.75, structure: 0.4 },
  melee: { light: 1, armored: 1, structure: 1 },
};

/** Every hit does at least this much, so armour never makes a unit immune. */
export const MIN_DAMAGE = 1;

export const COMBAT = {
  /** Idle / attack-moving units look for targets every this many ticks (staggered by unit id). */
  scanEveryTicks: 3,
  /** An automatically chosen target is chased until it is this many tiles beyond sight of where the chase began. */
  leashTiles: 3,
  /** Projectiles that have not landed after this many seconds disappear (safety net). */
  projectileMaxSeconds: 3,
  /** Spatial hash cell size in tiles (target search). */
  cellTiles: 8,
} as const;

/**
 * Damage dealt by one hit: base × multiplier(type, armour class), rounded,
 * minus flat armour, never below MIN_DAMAGE. Integer result.
 */
export function computeDamage(base: number, type: DamageType, armorClass: ArmorClass, armor: number): number {
  if (!(base > 0)) return 0;
  const scaled = Math.round(base * DAMAGE_MULTIPLIER[type][armorClass]);
  return Math.max(MIN_DAMAGE, scaled - Math.max(0, Math.floor(armor)));
}

/**
 * Are two owners enemies? Different owners are enemies, except that the
 * neutral world is nobody's enemy. (The infected, Phase 7, are everyone's enemy.)
 * Teams arrive with match modes (Phase 9/12).
 */
export function hostile(a: OwnerId, b: OwnerId, teams?: ReadonlyMap<OwnerId, number>): boolean {
  if (a === b || a === 'neutral' || b === 'neutral') return false;
  // Phase 9: players on the same team are allies.
  const ta = teams?.get(a);
  return ta === undefined || ta !== teams?.get(b);
}
